import { useTranslation } from 'react-i18next';
import { ArrowUpRight, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PROVIDER_NAMES, type ProviderView } from '../../../common/models/platform-costs.model';
import { money } from './cost-format';

/** One line per provider running out, most urgent first, with the way to top it up. */
export function AttentionBanner({ providers }: { providers: ProviderView[] }) {
  const { t } = useTranslation();
  const urgent = providers
    .filter((p) => p.level === 'low' || p.level === 'critical')
    .sort((a, b) => Number(b.level === 'critical') - Number(a.level === 'critical') || (a.runwayDays ?? 1e9) - (b.runwayDays ?? 1e9));
  if (!urgent.length) return null;

  return (
    <div className="flex flex-col divide-y rounded-xl border bg-card" role="alert" data-testid="costs-attention">
      {urgent.map((p) => {
        const critical = p.level === 'critical';
        const why =
          p.levelReason === 'reload'
            ? t('ADMIN_COSTS.ATTENTION.RELOAD')
            : p.levelReason === 'under-balance'
              ? t('ADMIN_COSTS.ATTENTION.UNDER_BALANCE')
              : p.runwayDays != null
                ? t('ADMIN_COSTS.ATTENTION.RUNWAY', { days: p.runwayDays < 10 ? p.runwayDays.toFixed(1) : Math.round(p.runwayDays) })
                : null;
        return (
          <div key={p.provider} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-2.5">
              <TriangleAlert className={cn('mt-0.5 size-4 shrink-0', critical ? 'text-destructive' : 'text-warning')} />
              <p className="text-sm">
                <span className="font-medium">
                  {t(critical ? 'ADMIN_COSTS.ATTENTION.CRITICAL' : 'ADMIN_COSTS.ATTENTION.LOW', { provider: PROVIDER_NAMES[p.provider], amount: money(p.balanceUsd) })}
                </span>{' '}
                {why && <span className="text-muted-foreground">{why}</span>}
              </p>
            </div>
            <Button asChild variant="outline" size="sm" className="shrink-0 self-start sm:self-auto">
              <a href={p.topUpUrl} target="_blank" rel="noreferrer">
                {t('ADMIN_COSTS.ATTENTION.TOP_UP')} {PROVIDER_NAMES[p.provider]}
                <ArrowUpRight />
              </a>
            </Button>
          </div>
        );
      })}
    </div>
  );
}
